import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import { useEditPage } from "@/hooks/useEditPage";
import ErrorBanner from "@/components/common/ErrorBanner";
import { RichTextEditor } from "@/components/editor/RichTextEditor";
import { Controller } from "react-hook-form";

interface WikiForm {
  title: string;
  destinationId: string | null;
  category: string;
  body: string;
  status: string;
}

interface DestinationLite {
  id: string;
  name: string;
}

export default function WikiEditPage() {
  const { data: destData } = useQuery({
    queryKey: ["destinations-lite"],
    queryFn: () => api.get<ApiResponse<DestinationLite[]>>("/api/admin/destinations"),
  });
  const destinations = destData?.data || [];

  const { isNew, isLoading, form, saveMutation, onSubmit, itemData } = useEditPage<WikiForm>({
    resourceKey: "wiki-page",
    listKey: "wiki",
    apiPath: "/api/admin/wiki",
    defaultValues: {
      title: "",
      destinationId: null,
      category: "general",
      body: "",
      status: "draft",
    },
    navigateTo: "/wiki",
    transformOnLoad: (w) => ({
      title: w.title || "",
      destinationId: w.destinationId || null,
      category: w.category || "general",
      body: w.body || "",
      status: w.status || "draft",
    }),
    transformOnSave: (f) => ({ ...f, destinationId: f.destinationId || null }),
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = form;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/wiki" className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700">
            <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isNew ? "New wiki page" : "Edit wiki page"}
            </h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              {isNew ? "Internal playbook / knowledge base" : `Editing: ${itemData?.title}`}
            </p>
          </div>
        </div>
        <button type="submit" form="wiki-form" disabled={isSubmitting} className="btn-primary">
          {isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" /> Saving...
            </>
          ) : (
            <>
              <Save className="h-5 w-5" /> Save
            </>
          )}
        </button>
      </div>

      <ErrorBanner error={saveMutation.error} />

      <form id="wiki-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="card space-y-4">
          <div>
            <label className="label">Title</label>
            <input
              {...register("title", { required: "Title is required" })}
              className="input"
              placeholder="e.g. Hanoi — local contacts & gotchas"
            />
            {errors.title && <p className="mt-1 text-sm text-red-500">{errors.title.message}</p>}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="label">Destination</label>
              <select {...register("destinationId")} className="input">
                <option value="">— General (all) —</option>
                {destinations.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Category</label>
              <select {...register("category")} className="input">
                <option value="general">General</option>
                <option value="playbook">Playbook</option>
                <option value="contacts">Contacts</option>
                <option value="sop">SOP</option>
              </select>
            </div>
            <div>
              <label className="label">Status</label>
              <select {...register("status")} className="input">
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
          </div>

          <div>
            <label className="label">Content</label>
            <Controller
              name="body"
              control={control}
              render={({ field }) => (
                <RichTextEditor
                  content={field.value || ""}
                  onChange={field.onChange}
                  placeholder="Write the playbook…"
                />
              )}
            />
          </div>
        </div>
      </form>
    </div>
  );
}
